import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { Posting } from '@features/transactions/types/posting'
import { Transaction } from '@features/transactions/types/transaction'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { fetchTransactions } from '../../../../../query-api'

interface UseFetchTransactionVoidPairProps {
  isVoid?: boolean
  isVoided?: boolean
  originatorReference?: string | null
  originalPostings?: Posting[]
  transactionId: string
}

interface UseFetchTransactionVoidPairResponse {
  isLoading: boolean
  pairTransaction: Transaction | undefined
}

const toComparablePostings = (postings: Posting[]) =>
  sortBy(
    postings.map(({ accountId, amount, currencyId }) => ({ accountId, amount, currencyId })),
    ['accountId', 'amount', 'currencyId'],
  )

const arePostingsMatching = (postingsA: Posting[], postingsB: Posting[]) =>
  postingsA.length === postingsB.length && isEqual(toComparablePostings(postingsA), toComparablePostings(postingsB))

export const useFetchTransactionVoidPair = ({
  isVoid,
  isVoided,
  originatorReference,
  originalPostings,
  transactionId,
}: UseFetchTransactionVoidPairProps): UseFetchTransactionVoidPairResponse => {
  const { organization } = useUserOrganization()
  const isVoidedEntry = Boolean(isVoid || isVoided)

  const { data, isLoading } = useQuery(
    [QueryKeys.Transaction, organization?.id, originatorReference],
    () =>
      fetchTransactions({
        organizationId: organization?.id,
        originatorReference: originatorReference as string,
        withPostings: true,
        withVoided: true,
      }),
    { enabled: isVoidedEntry && !!organization?.id && !!originatorReference },
  )

  const pairTransaction = useMemo(() => {
    if (!isVoidedEntry || !originalPostings?.length) {
      return undefined
    }
    const pair = data?.transactions.find(({ id, isVoid: candidateIsVoid, isVoided: candidateIsVoided, postings }) => {
      /* For every isVoid transaction, isVoided sister is created.
      We find pair by matching this state and same postings */
      const isPairVoidState = isVoided ? candidateIsVoid : candidateIsVoided
      const arePostingsEqual = postings ? arePostingsMatching(originalPostings, postings) : false

      return id !== transactionId && isPairVoidState && arePostingsEqual
    })

    return pair
  }, [data?.transactions, isVoided, isVoidedEntry, originalPostings, transactionId])

  return { isLoading: isVoidedEntry && isLoading, pairTransaction }
}
